import { Link } from 'react-router-dom';
import { Users } from 'lucide-react';
import { PanelCard, EmptyState, Initials } from '../../../components/ui';
import type { StudentListRow } from '../../../api/types';
import { personName } from '../../../lib/names';
import { gradeSectionLabel } from './format';

const SIGNAL = { trend: 'falling scores', recency: 'inactive', weak_chapters: 'weak chapters', engagement: 'low study time' } as const;
const MAX_ROWS = 5;

/** The five highest-risk students in the high or medium band, riskiest first. */
export function supportRows(students: StudentListRow[]): StudentListRow[] {
  return students
    .filter((s) => (s.risk_band === 'high' || s.risk_band === 'medium') && s.risk_score != null)
    .sort((a, b) => (b.risk_score as number) - (a.risk_score as number))
    .slice(0, MAX_ROWS);
}

export default function SupportCard({ students, className = '' }: { students: StudentListRow[]; className?: string }) {
  const rows = supportRows(students);
  return (
    <PanelCard className={className} title="Who needs support" actions={<Link to="/students" className="text-[13px] font-semibold text-muted-2 no-underline hover:text-ink hover:underline">All →</Link>}>
      {rows.length === 0 ? <EmptyState compact icon={<Users size={22} />} title="No students flagged right now" /> : (
        <ul>
          {rows.map((s) => {
            const name = personName(s) || 'Student';
            const high = s.risk_band === 'high';
            const why = s.risk_top_signal ? SIGNAL[s.risk_top_signal] : null;
            const place = gradeSectionLabel(s.grade_name, s.section);
            return (
              <li key={s.id} className="border-b border-hairline last:border-0">
                <Link to={`/students/profile/${s.id}`} className="flex items-center gap-3 py-[11px] text-ink no-underline hover:text-ink">
                  <Initials name={name} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{name}</span>
                    <span className="text-xs text-muted">{[place, why].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${high ? 'bg-danger-bg text-danger-ink' : 'bg-warning-bg text-warning-ink'}`}>
                    <span className="font-mono font-medium">{Math.round(s.risk_score as number)}</span>&nbsp;{high ? 'High' : 'Med'}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </PanelCard>
  );
}
